import type { MigrationRowData, MigrationStatusKey } from "./constants";

/**
 * Decide a migration's status against a target selection.
 *
 * Precedence (highest first):
 *   contentChanged → "changed"
 *   any selected target failed → "failed"
 *   no targets selected → "applied" if everApplied else "new"
 *   all selected targets succeeded → "applied"
 *   some selected succeeded, others pending → "partial"
 *   no selected target succeeded → "new"
 */
export function deriveStatus(
  row: MigrationRowData,
  selectedDatabaseNames: string[],
): MigrationStatusKey {
  if (row.contentChanged) return "changed";

  if (selectedDatabaseNames.length === 0) {
    return row.everApplied ? "applied" : "new";
  }

  // A failure whose cause is "the change is already there" is not a failure.
  // Before this, such a target had no appliedTo entry at all (the server only
  // returned successes), so the migration rendered as brand NEW and operators
  // re-ran it — producing another identical error, indefinitely.
  const failures = selectedDatabaseNames.filter((db) => row.appliedTo[db]?.status === "failed");
  const realFailures = failures.filter(
    (db) => row.appliedTo[db]?.errorKind !== "already_applied",
  );
  if (realFailures.length > 0) return "failed";

  if (failures.length > 0) {
    // Every failure is an already-present one. If the remaining targets all
    // succeeded, the migration is effectively in place everywhere — but say
    // "already present" so a human confirms rather than the UI asserting it.
    return "already-present";
  }

  const successCount = selectedDatabaseNames.filter(
    (db) => row.appliedTo[db]?.status === "success",
  ).length;

  if (successCount === selectedDatabaseNames.length) return "applied";
  if (successCount === 0) return "new";
  return "partial";
}
